import React, { FC } from 'react';
import {
  ButtonAsWrapper,
  CustomBorderProps,
  DetailTextLarge,
  FlexCol,
  FlexRow,
  H2Text,
  H4Text,
} from '@minecraft.atoms';
import { FormattedPriceMonth, useTranslation } from '@blocs.i18n';
import { DesignSystemTheme } from '@minecraft.themes';
import { VerticalPlanProps } from './types';
import { TemperatureValues } from '../enums/plans';

type PlanFeatureCardProps = {
  plan?: VerticalPlanProps['plan'];
  children?: React.ReactNode;
  title?: string;
  billed?: string;
  price?: number;
  billingCode?: string;
  temperature?: TemperatureValues;
  countryCode?: string;
  disableTrial?: boolean;
  isFreePlan?: boolean;
  setSelectedPlan?: (plan: VerticalPlanProps['plan']) => void;
  isSelectedPlan?: boolean;
};

const PlanFeatureCardHeader: FC<Omit<PlanFeatureCardProps, 'children' | 'price'>> = ({
  billingCode,
  title,
  billed,
  plan,
  temperature,
  countryCode,
  disableTrial,
  isFreePlan,
}) => {
  const { t } = useTranslation();
  const Text = isFreePlan ? H2Text : H4Text;
  return (
    <FlexRow
      w="full"
      className="cn_atom_px-3 cn_atom_py-5"
      borderRadius="sm"
      role="list"
      bgColor={`${temperature}Paywall` as keyof DesignSystemTheme['bgColors']}
      aria-label={`${billingCode} plan options`}
      alignItems="center"
    >
      <FlexCol ml="4" gap="3" w="full">
        <FlexRow justifyContent="space-between" gap="3" alignItems="center">
          <Text fontWeight="semibold" color={`${temperature}Paywall` as keyof DesignSystemTheme['textColors']}>
            {title}
          </Text>
        </FlexRow>
        {!isFreePlan && (
          <>
            {disableTrial ? (
              <FlexRow justifyContent="space-between" gap="1" alignItems="flex-end">
                <FormattedPriceMonth
                  price={Number(plan?.price)}
                  planCurrencyCode={plan?.currencyCode}
                  countryCode={countryCode}
                  planTermCode={plan?.term?.code}
                  PriceComponent={<H2Text fontWeight="medium" color="inverted" />}
                  LabelComponent={<DetailTextLarge color="inverted" />}
                  isSingleItemPrice={false}
                />
              </FlexRow>
            ) : (
              <FlexCol gap="1" w="full">
                <H2Text as="h2" fontWeight="bold" color="inverted">
                  {t('common:paywall.checkout.upsellFree', { days: plan?.trialLengthDays ?? 14 })}
                </H2Text>
                <DetailTextLarge align="center" color="inverted">
                  {billed}
                </DetailTextLarge>
              </FlexCol>
            )}
          </>
        )}
      </FlexCol>
    </FlexRow>
  );
};

export const PlanFeatureCard: FC<PlanFeatureCardProps> = React.memo(
  ({
    children,
    plan,
    billed,
    title,
    billingCode,
    temperature,
    countryCode,
    disableTrial,
    isFreePlan,
    setSelectedPlan,
    isSelectedPlan,
  }) => {
    const customBorderY = {
      borderColor: `${temperature}Paywall`,
      borderSize: isFreePlan ? 0.18 : 0.12,
      borderStyle: 'solid',
    } as CustomBorderProps;

    const customBorderX = {
      borderColor: `${temperature}Paywall`,
      borderSize: isFreePlan ? 0.18 : 0.12,
      borderStyle: 'solid',
    } as CustomBorderProps;

    return (
      <ButtonAsWrapper
        onClick={() => {
          setSelectedPlan?.(plan);
        }}
        aria-label={`${billingCode} plan wrapper`}
        data-testid={`plan-option-container-${billingCode}`}
        style={{
          width: '100%',
        }}
      >
        <FlexCol className="cn_overflow-hidden" borderRadius="sm" w="full">
          <FlexCol
            w="full"
            className={isSelectedPlan ? 'cn_atom_p-2' : 'cn_atom_p-0'}
            borderRadius="sm"
            grow={0}
            customBorders={
              isSelectedPlan
                ? {
                    top: customBorderY,
                    left: customBorderX,
                    right: customBorderX,
                    bottom: customBorderY,
                  }
                : undefined
            }
          >
            <PlanFeatureCardHeader
              plan={plan}
              billingCode={billingCode}
              title={title}
              billed={billed}
              temperature={temperature}
              countryCode={countryCode}
              disableTrial={disableTrial}
              isFreePlan={isFreePlan}
            />
            {isSelectedPlan && <>{children}</>}
          </FlexCol>
        </FlexCol>
      </ButtonAsWrapper>
    );
  }
);
